import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import IsoDatePicker from '../../schedule/components/IsoDatePicker'
import type { CostApplicationInvoice } from '../types'

interface Props {
  open: boolean
  invoice: CostApplicationInvoice | null
  onOpenChange: (open: boolean) => void
  /** Resolves true when the server accepted it; the dialog stays open on failure. */
  onSubmit: (input: { number: string; issueDate: string; dueDate: string | null }) => Promise<boolean>
}

/** The invoice the contractor issued to the investor for a certified application: number and dates, nothing more. */
const InvestorInvoiceDialog: React.FC<Props> = ({ open, invoice, onOpenChange, onSubmit }) => {
  const { t } = useTranslation()
  const [number, setNumber] = useState('')
  const [issueDate, setIssueDate] = useState<string | null>(null)
  const [dueDate, setDueDate] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  useEffect(() => {
    if (!open) return
    setNumber(invoice?.number ?? '')
    setIssueDate(invoice?.issueDate ?? null)
    setDueDate(invoice?.dueDate ?? null)
    setSubmitting(false)
    // Only on opening: a background reload must not wipe what is being typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  const orderProblem = !!issueDate && !!dueDate && dueDate < issueDate
  const valid = !!number.trim() && !!issueDate && !orderProblem
  const submit = async () => {
    if (!valid || submitting || !issueDate) return
    setSubmitting(true)
    const ok = await onSubmit({ number: number.trim(), issueDate, dueDate })
    setSubmitting(false)
    if (ok) onOpenChange(false)
  }
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!submitting) onOpenChange(next) }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('CostControl_application_investor_invoice_title')}</DialogTitle>
          <DialogDescription>{t('CostControl_application_investor_invoice_description')}</DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); submit() }}>
          <div className="space-y-1.5">
            <Label htmlFor="cc-inv-number">{t('CostControl_invoice_field_number')} *</Label>
            <Input id="cc-inv-number" value={number} onChange={(e) => setNumber(e.target.value)} autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cc-inv-issue">{t('CostControl_invoice_field_issueDate')} *</Label>
            <IsoDatePicker id="cc-inv-issue" value={issueDate} onChange={setIssueDate} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cc-inv-due">{t('CostControl_invoice_field_dueDate')}</Label>
            <IsoDatePicker id="cc-inv-due" value={dueDate} onChange={setDueDate} clearable invalid={orderProblem} />
            {orderProblem && <p role="alert" className="text-sm text-destructive">{t('CostControl_error_dates_order')}</p>}
          </div>
          <div className="flex items-center justify-end gap-2 border-t pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>{t('Common_cancel')}</Button>
            <Button type="submit" disabled={!valid || submitting}>
              {submitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
              {t('Common_save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
export default InvestorInvoiceDialog
